import React, { useState, useRef } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Form,
    Input,
    DatePicker,
    Switch,
    InputNumber,
    message,
    Upload,
    Alert,
    Divider,
    Table,
    Tag,
    Row,
    Col,
    Modal,
    Progress,
    Radio,
    Select
} from 'antd';
import dayjs from 'dayjs';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    AppstoreAddOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    RobotOutlined,
    SendOutlined
} from '@ant-design/icons';
import { TrainingType } from '../types/training';
import {
    createMoodleProgram,
    createMoodleQuiz,
    getMoodleProgramDetails,
} from '../services/moodleService';
import {
    adminGenerateAIQuestions,
} from '../services/adminService';
import { parseModuleId, validateBlobSafeName } from '../utils/moduleUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUserEmail } from '../services/trainingService';
import AICurriculumModal, { CurriculumResult } from '../components/AICurriculumModal';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface SubjectiveQuestion {
    id: string; // internal mapping
    question: string;
    answer: string;
    codeSnippet: string;
}

interface ModuleEntry {
    id: string;
    name: string;
    description: string;
    difficulty: string;
    questionCount: number;
    estimatedTime: number;
    attemptLimit: number; // 0 = unlimited
    questions: SubjectiveQuestion[];
    generated: boolean;
    generating: boolean;
    extraPrompt?: string;
}

const CreateSubjectiveProgram: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState<ModuleEntry[]>([]);

    // Progress Modal State
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    // Questions Review Modal State
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [activeReviewModuleId, setActiveReviewModuleId] = useState<string | null>(null);

    // AI Curriculum Designer State
    const [curriculumModalOpen, setCurriculumModalOpen] = useState(false);


    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const publishIntentRef = useRef(false);

    // Watch the form target title.
    const trainingName = Form.useWatch('trainingName', form);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const copyFromId = params.get('copyFrom');
        if (copyFromId) {
            handleDuplicatePreFill(copyFromId);
        }
    }, [location.search]);

    const handleDuplicatePreFill = async (trainingId: string) => {
        try {
            setLoading(true);
            const data = await getMoodleProgramDetails(trainingId);
            if (!data) {
                message.error('Source program not found in Moodle');
                return;
            }

            // 1. Pre-fill form basics
            form.setFieldsValue({
                trainingName: `${data.trainingName} - Copy`,
                trainingType: data.trainingType || TrainingType.OPEN,
                passingPercentage: data.passingPercentage,
                estimatedEffortHours: data.estimatedEffortHours,
                trainingLink: data.trainingLink,
                notificationVersion: '1.0',
                trainingId: '',
                dateRange: null,
                isEvergreen: true,
                audienceEmails: data.audienceEmails,
                stakeholderEmails: data.stakeholderEmails
            });

            // 2. Simplify for POC duplication
            setModules([]);
            message.success(`Metadata loaded from Moodle for ${data.trainingName}. Please re-add modules.`);
        } catch (error) {
            console.error('Error pre-filling duplicate:', error);
            message.error('Failed to load data from Moodle for duplication');
        } finally {
            setLoading(false);
        }
    };

    const addModuleRow = () => {
        const newId = Math.random().toString(36).substr(2, 9);
        setModules([...modules, {
            id: newId,
            name: '',
            description: '',
            difficulty: 'Medium',
            questionCount: 5,
            estimatedTime: 30,
            attemptLimit: 0,
            questions: [],
            generated: false,
            generating: false,
            extraPrompt: ''
        }]);
    };

    const removeModuleRow = (id: string) => {
        setModules(modules.filter(m => m.id !== id));
    };

    const updateModuleField = (id: string, field: keyof ModuleEntry, value: any) => {
        setModules(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleApplyCurriculum = (aiPlan: CurriculumResult) => {
        // Map the AI suggestions to internal ModelEntry rows
        const newModules: ModuleEntry[] = aiPlan.modules.map(mod => ({
            id: Math.random().toString(36).substr(2, 9),
            name: mod.name,
            description: mod.description,
            difficulty: 'Medium',
            questionCount: 3,
            estimatedTime: 30,
            attemptLimit: 0,
            questions: [],
            generated: false,
            generating: false,
            extraPrompt: ''
        }));

        setModules(prev => [...prev, ...newModules]);

        // Auto-fill the trainingLink if provided AND it's not currently populated
        const currentLink = form.getFieldValue('trainingLink');
        if (aiPlan.suggestedLink && !currentLink) {
            form.setFieldsValue({ trainingLink: aiPlan.suggestedLink });
        }

        // Auto-fill estimatedEffortHours if provided
        if (aiPlan.estimatedEffortHours) {
            form.setFieldsValue({ estimatedEffortHours: aiPlan.estimatedEffortHours });
        }
    };

    const handleGenerateForModule = async (module: ModuleEntry) => {
        const currentFormValues = form.getFieldsValue();
        const trainingName = currentFormValues.trainingName || "General Training";

        if (!module.name || !module.description) {
            message.warning("Please provide a Module Name and Description/Topic Focus before generating.");
            return;
        }

        // Initialize UI for generation
        setModules(prev => prev.map(m =>
            m.id === module.id ? { ...m, generating: true, generated: false, questions: [] } : m
        ));

        try {
            // Fetch questions and suggested time in one batch
            const aiData = await adminGenerateAIQuestions(
                `Training Program: ${trainingName}. Module Name: ${module.name}. Module Focus: ${module.description}. Generate ${module.questionCount} distinct questions.`,
                module.difficulty,
                module.questionCount,
                module.extraPrompt
            );

            // Format with internal IDs for React table
            const formattedQs = aiData.questions.slice(0, module.questionCount).map((q: any) => ({
                id: Math.random().toString(36).substr(2, 9),
                question: q.Question || q.question || '',
                answer: q.Answer || q.answer || '',
                codeSnippet: q.CodeSnippet || q.codeSnippet || ''
            }));

            // Finish generation
            setModules(prev => prev.map(m =>
                m.id === module.id ? {
                    ...m,
                    questions: formattedQs,
                    estimatedTime: aiData.estimatedTimeMinutes || m.estimatedTime,
                    generated: true,
                    generating: false
                } : m
            ));

            message.success(`Generated questions and estimated time (${aiData.estimatedTimeMinutes} mins) for ${module.name}`);
        } catch (error: any) {
            message.error(error.message || "Failed to generate questions");
            setModules(prev => prev.map(m =>
                m.id === module.id ? { ...m, generating: false } : m
            ));
        }
    };

    const openReviewModal = (moduleId: string) => {
        setActiveReviewModuleId(moduleId);
        setReviewModalVisible(true);
    };

    const updateQuestionText = (questionId: string, field: 'question' | 'answer' | 'codeSnippet', value: string) => {
        if (!activeReviewModuleId) return;
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: m.questions.map(q => q.id === questionId ? { ...q, [field]: value } : q)
                };
            }
            return m;
        }));
    };

    const removeQuestion = (questionId: string) => {
        if (!activeReviewModuleId) return;
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: m.questions.filter(q => q.id !== questionId)
                };
            }
            return m;
        }));
    };

    const addManualQuestion = () => {
        if (!activeReviewModuleId) return;
        const newQuestion: SubjectiveQuestion = {
            id: Math.random().toString(36).substr(2, 9),
            question: '',
            answer: '',
            codeSnippet: ''
        };
        setModules(prev => prev.map(m => {
            if (m.id === activeReviewModuleId) {
                return {
                    ...m,
                    questions: [...m.questions, newQuestion]
                };
            }
            return m;
        }));
    };

    const handleFinish = async (values: any) => {
        try {
            if (modules.length === 0) {
                throw new Error('Please add at least one module.');
            }

            const ungenerated = modules.filter(m => !m.generated || m.questions.length === 0);
            if (ungenerated.length > 0) {
                throw new Error('Please generate questions for all modules before submitting.');
            }

            setTotalSteps(modules.length + 1);
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Initializing creation process...');
            setProgressModalVisible(true);
            setLoading(true);

            // 1. Create the Training Program
            const cleanEmails = (str: string) => str ? str.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';

            const trainingPayload = {
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                active: true,
                startDate: values.isEvergreen ? null : values.dateRange[0].format('YYYY-MM-DD'),
                endDate: values.isEvergreen ? null : values.dateRange[1].format('YYYY-MM-DD'),
                passingPercentage: values.passingPercentage,
                trainingLink: values.trainingLink || '',
                estimatedEffortHours: values.estimatedEffortHours,
                audienceEmails: cleanEmails(values.audienceEmails),
                stakeholderEmails: cleanEmails(values.stakeholderEmails),
                assessments: modules.map(m => `${m.name.trim()} [${m.description.trim()}] #T${m.estimatedTime || 30} #A${m.attemptLimit || 0}`).join(' | '),
                notificationVersion: values.notificationVersion || '1.0',
                trainingType: values.trainingType,
                visible: publishIntentRef.current ? 1 : 0,
                createdby: await getCurrentUserEmail()
            };

            setProgressMessage('Creating Training Program in Moodle...');
            const programResult = await createMoodleProgram(trainingPayload as any);
            const courseId = programResult.id;
            setCurrentStep(1);

            // 2. Create Moodle Quizzes for each module
            for (let i = 0; i < modules.length; i++) {
                const module = modules[i];
                setProgressMessage(`Creating Moodle Quiz for module: ${module.name} (${i + 1}/${modules.length})...`);
                
                // For subjective, intro can contain the criteria or topic focus
                await createMoodleQuiz(
                    courseId,
                    module.name,
                    `Assessment: ${module.description}.`,
                    i + 1,
                    module.questions,
                    module.estimatedTime || 30,
                    module.attemptLimit || 0
                );
                
                setCurrentStep(prev => prev + 1);
            }

            setProcessStatus('success');
            setProgressMessage(publishIntentRef.current
                ? 'Program published successfully! Redirecting...'
                : 'Draft saved! You can publish it later from the admin dashboard. Redirecting...');

            setTimeout(() => {
                navigate('/admin');
            }, 1500);

        } catch (error: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${error.message || 'Failed to create training program'}`);
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        if (processStatus === 'exception') {
            setProgressModalVisible(false);
        }
    };

    const columns = [
        {
            title: 'Module Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
            render: (_: any, record: ModuleEntry) => (
                <Input
                    placeholder="e.g. React Basics"
                    value={record.name}
                    onChange={(e) => updateModuleField(record.id, 'name', e.target.value)}
                />
            )
        },
        {
            title: 'Topic Focus / Description',
            dataIndex: 'description',
            key: 'description',
            width: '30%',
            render: (_: any, record: ModuleEntry) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                        placeholder="e.g. Hooks and State Management"
                        value={record.description}
                        onChange={(e) => updateModuleField(record.id, 'description', e.target.value)}
                    />
                    <Input
                        placeholder="Extra question prompt (e.g. coding Qs only) (optional)"
                        value={record.extraPrompt}
                        onChange={(e) => updateModuleField(record.id, 'extraPrompt', e.target.value)}
                        size="small"
                    />
                </Space>
            )
        },
        {
            title: 'Criteria',
            key: 'criteria',
            width: '20%',
            render: (_: any, record: ModuleEntry) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                        value={record.difficulty}
                        onChange={(val) => updateModuleField(record.id, 'difficulty', val)}
                        style={{ width: '100%' }}
                    >
                        <Option value="Easy">Easy</Option>
                        <Option value="Medium">Medium</Option>
                        <Option value="Hard">Hard</Option>
                    </Select>
                    <InputNumber
                        min={1}
                        max={20}
                        value={record.questionCount}
                        onChange={(val) => updateModuleField(record.id, 'questionCount', val)}
                        style={{ width: '100%' }}
                        addonBefore="Qty"
                    />
                </Space>
            )
        },
        {
            title: 'AI Generator',
            key: 'generator',
            width: '25%',
            render: (_: any, record: ModuleEntry) => (
                <Space direction="vertical">
                    <Button
                        type={record.generated ? 'default' : 'primary'}
                        icon={<RobotOutlined />}
                        loading={record.generating}
                        onClick={() => handleGenerateForModule(record)}
                        disabled={modules.some(m => m.generating) && !record.generating}
                    >
                        {record.generating
                            ? 'Generating'
                            : record.generated ? 'Regenerate' : 'Generate AI Qs'}
                    </Button>
                    {record.generated && (
                        <Space>
                            <Tag color="success" icon={<CheckCircleFilled />}>{record.questions.length} Ready</Tag>
                            <Button type="link" size="small" onClick={() => openReviewModal(record.id)}>Review / Edit</Button>
                        </Space>
                    )}
                </Space>
            )
        },
        {
            title: 'Time (Mins)',
            dataIndex: 'estimatedTime',
            width: 100,
            render: (_: any, record: ModuleEntry) => (
                <InputNumber
                    min={1}
                    max={300}
                    value={record.estimatedTime}
                    onChange={(val) => updateModuleField(record.id, 'estimatedTime', val || 30)}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Max Attempts',
            dataIndex: 'attemptLimit',
            width: 110,
            render: (_: any, record: ModuleEntry) => (
                <InputNumber
                    min={0}
                    max={99}
                    value={record.attemptLimit}
                    onChange={(val) => updateModuleField(record.id, 'attemptLimit', val ?? 0)}
                    placeholder="0=∞"
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: '',
            key: 'action',
            width: 50,
            render: (_: any, record: ModuleEntry) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeModuleRow(record.id)} />
            )
        }
    ];

    const activeReviewModule = modules.find(m => m.id === activeReviewModuleId);
    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

            <AICurriculumModal
                open={curriculumModalOpen}
                onClose={() => setCurriculumModalOpen(false)}
                onApply={handleApplyCurriculum}
                initialTopic={trainingName}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    active: true,
                    passingPercentage: 80,
                    notificationVersion: '1.0',
                    trainingType: TrainingType.OPEN,
                    isEvergreen: true
                }}
            >
                <Card title="1. Program Details" style={{ marginBottom: '24px', borderRadius: '12px' }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Training ID" name="trainingId" rules={[{ required: true }, { validator: validateBlobSafeName }]}>
                                <Input placeholder="TR-202X-001" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => (value !== undefined && value !== null && `${value}` !== '') ? `${value}%` : ''} parser={value => value ? parseFloat(value.replace('%', '')) : 0 as any} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row align="bottom">
                        <Col flex="auto">
                            <Form.Item label="Training Program Name" name="trainingName" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <Input 
                                    placeholder="Enter full program name (e.g. Advanced C++ Data Structures)" 
                                    size="large" 
                                    maxLength={250}
                                    showCount
                                />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<RobotOutlined />}
                                size="large"
                                style={{ marginLeft: 16, backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                                onClick={() => setCurriculumModalOpen(true)}
                                disabled={!trainingName?.trim()}
                            >
                                ✨ Auto-Generate Curriculum
                            </Button>
                        </Col>
                    </Row>
                    <br />

                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType || prevValues.isEvergreen !== currentValues.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const trainingType = getFieldValue('trainingType');
                                    const isEvergreen = getFieldValue('isEvergreen');

                                    // Auto-uncheck evergreen if they switch away from Open
                                    if (trainingType !== TrainingType.OPEN && isEvergreen) {
                                        // Use setTimeout to avoid pure render synchronous state updates warning
                                        setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    }

                                    return (
                                        <>
                                            {trainingType === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" />
                                                </Form.Item>
                                            )}

                                            {!isEvergreen || trainingType !== TrainingType.OPEN ? (
                                                <Form.Item
                                                    label="Training Period"
                                                    name="dateRange"
                                                    rules={[{ required: true, message: 'Please select a training period' }]}
                                                    extra="Users can only start training on or after the start date."
                                                >
                                                    <DatePicker.RangePicker
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                    />
                                                </Form.Item>
                                            ) : (
                                                <Alert
                                                    message="Evergreen Program"
                                                    description="This training has no fixed dates and will be available immediately."
                                                    type="info"
                                                    showIcon
                                                    style={{ marginBottom: 24 }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink">
                                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="https://..." style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}>
                                    <Text strong>Open for All</Text>
                                    <br />
                                    <Text type="secondary">No nominations required. Anyone can join and take the assessment anytime.</Text>
                                </Radio>
                                <Radio value={TrainingType.REQUESTABLE}>
                                    <Text strong>Nominated + Request to Join</Text>
                                    <br />
                                    <Text type="secondary">Specific people are nominated. Others can request to join before the start date.</Text>
                                </Radio>
                                <Radio value={TrainingType.PRIVATE}>
                                    <Text strong>Private (Invite Only)</Text>
                                    <br />
                                    <Text type="secondary">Only people explicitly nominated can see and access this program.</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType}>
                        {({ getFieldValue }) => {
                            const type = getFieldValue('trainingType');
                            if (type === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Participants (Emails, semicolon separated)" name="audienceEmails" rules={[{ required: true }]}>
                                            <TextArea rows={4} placeholder="user1@company.com; user2@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders (Emails, semicolon separated)" name="stakeholderEmails" rules={[{ required: true }]}>
                                            <TextArea rows={4} placeholder="manager@company.com; director@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>
                </Card>

                <Card
                    title="2. Module Setup & Question Generation"
                    style={{ marginBottom: '24px', borderRadius: '12px' }}
                    extra={
                        <Button type="dashed" onClick={addModuleRow} icon={<PlusOutlined />} style={{ borderColor: '#1890ff', color: '#1890ff' }}>
                            Add Module
                        </Button>
                    }
                >
                    <Alert
                        message="AI Generation Workflow"
                        description="Add modules, describe the topic, and select difficulty. Click 'Generate AI Qs' for each module. You can review and edit the generated questions before final submission."
                        type="info"
                        showIcon
                        style={{ marginBottom: '24px', borderRadius: '8px' }}
                    />

                    <Table
                        dataSource={modules}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        locale={{ emptyText: <div style={{ padding: '20px', color: '#999' }}>Click "Add Module" to start adding curriculum.</div> }}
                        bordered
                    />
                </Card>

                <div style={{ textAlign: 'right', marginTop: '32px', marginBottom: '50px' }}>
                    <Space size="large">
                        <Button onClick={() => navigate('/admin')} size="large" disabled={loading}>Cancel</Button>
                        <Button
                            size="large"
                            icon={<SaveOutlined />}
                            disabled={loading}
                            onClick={() => { publishIntentRef.current = false; form.submit(); }}
                        >
                            Save as Draft
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            loading={loading}
                            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
                            onClick={() => { publishIntentRef.current = true; form.submit(); }}
                        >
                            Publish
                        </Button>
                    </Space>
                </div>
            </Form>

            {/* Review Modal */}
            <Modal
                title={`Review Questions: ${activeReviewModule?.name}`}
                open={reviewModalVisible}
                onCancel={() => setReviewModalVisible(false)}
                footer={[
                    <Button key="add" icon={<PlusOutlined />} onClick={addManualQuestion} style={{ float: 'left' }}>Add Question</Button>,
                    <Button key="done" type="primary" onClick={() => setReviewModalVisible(false)}>Done Reviewing</Button>
                ]}
                width={1000}
                centered
            >
                {activeReviewModule?.questions.map((q, index) => (
                    <Card
                        key={q.id}
                        style={{ marginBottom: 16, backgroundColor: '#f9f9f9', border: '1px solid #e8e8e8' }}
                        size="small"
                        extra={
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => removeQuestion(q.id)}
                            >
                                Remove
                            </Button>
                        }
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Text strong>Q{index + 1}: Question Detail</Text>
                            <TextArea
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                value={q.question}
                                onChange={(e) => updateQuestionText(q.id, 'question', e.target.value)}
                            />

                            <Text strong>Expected Answer / Criteria</Text>
                            <TextArea
                                autoSize={{ minRows: 3, maxRows: 8 }}
                                value={q.answer}
                                onChange={(e) => updateQuestionText(q.id, 'answer', e.target.value)}
                            />

                            <Text strong>Code Snippet (Optional)</Text>
                            <TextArea
                                style={{ fontFamily: 'monospace' }}
                                autoSize={{ minRows: 1, maxRows: 6 }}
                                value={q.codeSnippet}
                                placeholder="Any reference code goes here..."
                                onChange={(e) => updateQuestionText(q.id, 'codeSnippet', e.target.value)}
                            />
                        </Space>
                    </Card>
                ))}
            </Modal>

            {/* Progress / Blocking Modal */}
            <Modal
                title={
                    <Space>
                        {processStatus === 'active' && <LoadingOutlined />}
                        <span>Saving to Moodle</span>
                    </Space>
                }
                open={progressModalVisible}
                closable={processStatus === 'exception'}
                maskClosable={false}
                onCancel={processStatus === 'exception' ? handleCloseModal : undefined}
                footer={processStatus === 'exception' ? [<Button key="close" onClick={handleCloseModal}>Close</Button>] : null}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Progress type="circle" percent={percent} status={processStatus} strokeColor="#1890ff" />
                    <div style={{ marginTop: '24px' }}>
                        <Title level={4}>{processStatus === 'success' ? 'Completed!' : 'Saving to Database...'}</Title>
                        <Text>{progressMessage}</Text>
                    </div>
                </div>
            </Modal>
        </div >
    );
};

export default CreateSubjectiveProgram;
